import React, { useReducer, useCallback, useEffect, useMemo } from 'react';
import { useContext } from 'react';
import {
  clearAllAction,
  ExtraFilterReducer,
  FilterItem,
  FilterState,
  FilterValue,
  getFilterReducer,
  updateFilterAction,
  updateFilterLabelAction,
} from './Filter/filterReducer';

type OnFilterUpdate = <TFilterFields extends FilterState>(
  filterQuery: Record<
    keyof TFilterFields['filter'],
    FilterValue | FilterValue[]
  >
) => void;

export interface FilterProviderProps {
  onFilterUpdated?: OnFilterUpdate;
  defaultFilterValues?: Record<string, FilterValue | FilterValue[]>;
  extraFilterReducer?: ExtraFilterReducer;
}

type OnFilterItemChange = (
  name: string,
  value: FilterValue | FilterValue[],
  label?: string | string[]
) => void;

type OnFilterItemLabelChange = (
  name: string,
  label?: string | string[]
) => void;

export interface FilterItemProps {
  name: string;
  onChange?: OnFilterItemChange;
}

export interface FilterContext {
  onFilterItemChange: OnFilterItemChange;
  onFilterItemLabelChange: OnFilterItemLabelChange;
  onClearFilters: (keys: string[]) => void;
  getDefaultValueByName: (name: string) => FilterValue | FilterValue[];
  onTableSort: (type: string) => void;
  filterValues: Record<string, FilterValue | FilterValue[]>;
  filterLabels: Record<string, string | string[]>;
}

export const filterContext = React.createContext<FilterContext>({
  onFilterItemChange: () => {
    /* */
  },
  onFilterItemLabelChange: () => {
    /* */
  },
  onClearFilters: () => {
    //
  },
  onTableSort: () => {
    //
  },
  getDefaultValueByName: () => '',
  filterValues: {},
  filterLabels: {},
});

export const useFilter = () => {
  return useContext(filterContext);
};

export const FilterProvider = ({
  onFilterUpdated = () => {
    //
  },
  children,
  defaultFilterValues = {},
  extraFilterReducer,
}: React.PropsWithChildren<FilterProviderProps>) => {
  const defaultFilter = Object.entries(defaultFilterValues).reduce<
    Record<string, FilterItem>
  >((acc, [key, value]) => {
    acc[key] = { value: value, label: [] };
    return acc;
  }, {});

  const [state, dispatch] = useReducer(getFilterReducer(extraFilterReducer), {
    filter: defaultFilter || {},
    meta: {},
  });

  const stateString = JSON.stringify(state);

  const dispatchUpdateFilter = useCallback(
    (
      name: string,
      value: FilterValue | FilterValue[],
      label?: string | string[]
    ) => {
      dispatch(updateFilterAction(name, value, label));
    },
    [dispatch]
  );

  const dispatchUpdateFilterLabel = useCallback(
    (name: string, label?: string | string[]) => {
      dispatch(updateFilterLabelAction(name, label));
    },
    [dispatch]
  );

  const dispatchClearFilters = useCallback(
    (keys: string[]) => {
      dispatch(clearAllAction(keys));
    },
    [dispatch]
  );

  const getDefaultValueByName = useCallback(
    (name: string) => {
      return defaultFilter[name]?.value;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(defaultFilter)]
  );

  const filterValues = useMemo(() => {
    return Object.entries(state.filter).reduce<
      Record<string, FilterValue | FilterValue[]>
    >((acc, [key, filter]) => {
      acc[key] = filter.value;
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateString]);

  const filterLabels = useMemo(() => {
    return Object.entries(state.filter).reduce<
      Record<string, string | string[]>
    >((acc, [key, filter]) => {
      acc[key] = filter.label;
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateString]);

  const onTableSort = useCallback(
    (type: string) => {
      const [currentSort, currentDirection] =
        (filterValues['sort_by'] as string[]) || [];
      const newDirection =
        currentSort === type
          ? currentDirection === 'asc'
            ? 'desc'
            : null
          : 'asc';

      if (!newDirection) {
        dispatchUpdateFilter('sort_by', undefined);
      } else {
        dispatchUpdateFilter('sort_by', [type, newDirection]);
      }
    },
    [filterValues, dispatchUpdateFilter]
  );

  useEffect(() => {
    onFilterUpdated(filterValues);
    localStorage.setItem('projectsFilter', JSON.stringify(filterValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateString]);

  return (
    <filterContext.Provider
      value={{
        onFilterItemChange: dispatchUpdateFilter,
        onFilterItemLabelChange: dispatchUpdateFilterLabel,
        onClearFilters: dispatchClearFilters,
        onTableSort,
        getDefaultValueByName,
        filterValues,
        filterLabels,
      }}
    >
      {children}
    </filterContext.Provider>
  );
};
